import React, { useEffect, useState } from 'react';
import { CalendarIcon, DollarSignIcon, RepeatIcon, SearchIcon, UserIcon, CreditCardIcon, ClockIcon, LinkIcon, MailIcon, PhoneIcon } from 'lucide-react';
interface RecurringPaymentProps {
  onAmountChange: (amount: string) => void;
  onCurrencyChange: (currency: string) => void;
  onReferenceChange: (reference: string) => void;
  onSubscriptionModeChange?: (mode: 'existing' | 'new') => void;
}
export function RecurringPayment({
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSubscriptionModeChange
}: RecurringPaymentProps) {
  // Payment details
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  // Subscription details
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [billingCycles, setBillingCycles] = useState('');
  const [startDate, setStartDate] = useState('');
  const [trialDays, setTrialDays] = useState('0');
  // Customer selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    name: string;
    email: string;
    cards: Array<{
      id: string;
      last4: string;
      brand: string;
      expiry: string;
    }>;
  } | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  // Subscription mode
  const [subscriptionMode, setSubscriptionMode] = useState<'existing' | 'new'>('existing');
  // Customer email/phone for new subscription
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerName, setCustomerName] = useState('');
  // Notify parent component when subscription mode changes
  useEffect(() => {
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(subscriptionMode);
    }
  }, [subscriptionMode, onSubscriptionModeChange]);
  // Mock customer data
  const customers = [{
    id: 'cust_1',
    name: 'John Doe',
    email: 'john@example.com',
    cards: [{
      id: 'card_1',
      last4: '4242',
      brand: 'Visa',
      expiry: '04/25'
    }, {
      id: 'card_2',
      last4: '5555',
      brand: 'Mastercard',
      expiry: '07/24'
    }]
  }, {
    id: 'cust_2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    cards: [{
      id: 'card_3',
      last4: '1234',
      brand: 'Visa',
      expiry: '09/26'
    }]
  }];
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    setAmount(value);
    onAmountChange(value);
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrency(e.target.value);
    onCurrencyChange(e.target.value);
  };
  const handleReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReference(e.target.value);
    onReferenceChange(e.target.value);
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSelectedCustomer(null);
    setSelectedCard(null);
  };
  const handleSelectCustomer = (customer: (typeof customers)[0]) => {
    setSelectedCustomer(customer);
    setSelectedCard(null);
    setSearchTerm('');
  };
  const handleSelectCard = (cardId: string) => {
    setSelectedCard(cardId);
  };
  const handleSubscriptionModeChange = (mode: 'existing' | 'new') => {
    setSubscriptionMode(mode);
    if (onSubscriptionModeChange) {
      onSubscriptionModeChange(mode);
    }
  };
  // Filter customers based on search term
  const filteredCustomers = searchTerm ? customers.filter(customer => customer.name.toLowerCase().includes(searchTerm.toLowerCase()) || customer.email.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  return <div className="space-y-6" data-id="element-1654">
      {/* Subscription Mode Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1655">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1656">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-1657" />
          Subscription Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1658">
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'existing' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('existing')} data-id="element-1659">
            <div className="flex items-start space-x-3" data-id="element-1660">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-1661">
                <CreditCardIcon size={20} className="text-blue-600" data-id="element-1662" />
              </div>
              <div data-id="element-1663">
                <h3 className="font-medium text-gray-900" data-id="element-1664">Existing Customer</h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1665">
                  Create a subscription for an existing customer with a saved
                  payment method
                </p>
              </div>
            </div>
          </div>
          <div className={`border rounded-lg p-4 cursor-pointer transition-all ${subscriptionMode === 'new' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'}`} onClick={() => handleSubscriptionModeChange('new')} data-id="element-1666">
            <div className="flex items-start space-x-3" data-id="element-1667">
              <div className="p-2 bg-blue-100 rounded-md" data-id="element-1668">
                <LinkIcon size={20} className="text-blue-600" data-id="element-1669" />
              </div>
              <div data-id="element-1670">
                <h3 className="font-medium text-gray-900" data-id="element-1671">
                  New Subscription Link
                </h3>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1672">
                  Send a link to customer to start a new subscription
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Customer Selection (for existing customers) */}
      {subscriptionMode === 'existing' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1673">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1674">
            Customer Selection
          </h3>
          <div className="space-y-4" data-id="element-1675">
            <div data-id="element-1676">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1677">
                Search Customer
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1678">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1679">
                  <SearchIcon size={16} className="text-gray-400" data-id="element-1680" />
                </div>
                <input type="text" value={searchTerm} onChange={handleSearchChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search by name or email" data-id="element-1681" />
              </div>
            </div>
            {searchTerm && filteredCustomers.length > 0 && !selectedCustomer && <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-1682">
                  <ul className="divide-y divide-gray-200" data-id="element-1683">
                    {filteredCustomers.map(customer => <li key={customer.id} className="hover:bg-gray-50" data-id="element-1684">
                        <button type="button" onClick={() => handleSelectCustomer(customer)} className="w-full px-4 py-3 text-left flex items-center" data-id="element-1685">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-1686">
                            <UserIcon size={16} data-id="element-1687" />
                          </div>
                          <div data-id="element-1688">
                            <p className="text-sm font-medium text-gray-900" data-id="element-1689">
                              {customer.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1690">
                              {customer.email}
                            </p>
                          </div>
                        </button>
                      </li>)}
                  </ul>
                </div>}
            {selectedCustomer && <div className="bg-gray-50 rounded-md p-4" data-id="element-1691">
                <div className="flex items-center justify-between" data-id="element-1692">
                  <div className="flex items-center" data-id="element-1693">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mr-3" data-id="element-1694">
                      <UserIcon size={20} data-id="element-1695" />
                    </div>
                    <div data-id="element-1696">
                      <p className="text-sm font-medium text-gray-900" data-id="element-1697">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1698">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => setSelectedCustomer(null)} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-1699">
                    Change
                  </button>
                </div>
                <div className="mt-4" data-id="element-1700">
                  <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-1701">
                    Saved Payment Methods
                  </p>
                  <div className="space-y-2" data-id="element-1702">
                    {selectedCustomer.cards.map(card => <div key={card.id} onClick={() => handleSelectCard(card.id)} className={`border rounded-md p-3 cursor-pointer ${selectedCard === card.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/50'}`} data-id="element-1703">
                        <div className="flex items-center justify-between" data-id="element-1704">
                          <div className="flex items-center" data-id="element-1705">
                            <div className="mr-3" data-id="element-1706">
                              {card.brand === 'Visa' ? <CreditCardIcon size={20} className="text-blue-700" data-id="element-1707" /> : card.brand === 'Mastercard' ? <CreditCardIcon size={20} className="text-red-600" data-id="element-1708" /> : <CreditCardIcon size={20} className="text-gray-600" data-id="element-1709" />}
                            </div>
                            <div data-id="element-1710">
                              <p className="text-sm font-medium text-gray-900" data-id="element-1711">
                                {card.brand} •••• {card.last4}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-1712">
                                Expires {card.expiry}
                              </p>
                            </div>
                          </div>
                          <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center" data-id="element-1713">
                            {selectedCard === card.id && <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-1714"></div>}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>
        </div>}
      {/* Customer Information (for new subscription) */}
      {subscriptionMode === 'new' && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1715">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-1716">
            Customer Information
          </h3>
          <div className="space-y-4" data-id="element-1717">
            <div data-id="element-1718">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1719">
                Customer Name (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1720">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1721">
                  <UserIcon size={16} className="text-gray-400" data-id="element-1722" />
                </div>
                <input type="text" value={customerName} onChange={e => setCustomerName(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="John Doe" data-id="element-1723" />
              </div>
            </div>
            <div data-id="element-1724">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1725">
                Customer Email
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1726">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1727">
                  <MailIcon size={16} className="text-gray-400" data-id="element-1728" />
                </div>
                <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="customer@example.com" required data-id="element-1729" />
              </div>
            </div>
            <div data-id="element-1730">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1731">
                Customer Phone (Optional)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1732">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1733">
                  <PhoneIcon size={16} className="text-gray-400" data-id="element-1734" />
                </div>
                <input type="tel" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="+1 (555) 123-4567" data-id="element-1735" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1736">
                We'll send a subscription link to the customer via email or SMS
              </p>
            </div>
          </div>
        </div>}
      {/* Subscription Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1737">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1738">
          <RepeatIcon size={20} className="mr-2 text-blue-600" data-id="element-1739" />
          Subscription Details
        </h3>
        <div className="space-y-4" data-id="element-1740">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1741">
            <div data-id="element-1742">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1743">
                Amount per Billing Period*
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1744">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1745">
                  <DollarSignIcon size={16} className="text-gray-400" data-id="element-1746" />
                </div>
                <input type="text" value={amount} onChange={handleAmountChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" required data-id="element-1747" />
              </div>
            </div>
            <div data-id="element-1748">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1749">
                Currency
              </label>
              <select value={currency} onChange={handleCurrencyChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1750">
                <option value="USD" data-id="element-1751">USD - US Dollar</option>
                <option value="EUR" data-id="element-1752">EUR - Euro</option>
                <option value="GBP" data-id="element-1753">GBP - British Pound</option>
                <option value="JPY" data-id="element-1754">JPY - Japanese Yen</option>
                <option value="CAD" data-id="element-1755">CAD - Canadian Dollar</option>
                <option value="AUD" data-id="element-1756">AUD - Australian Dollar</option>
              </select>
            </div>
          </div>
          <div data-id="element-1757">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1758">
              Description*
            </label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Premium Membership" required data-id="element-1759" />
          </div>
          <div data-id="element-1760">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1761">
              Your Reference (Optional)
            </label>
            <input type="text" value={reference} onChange={handleReferenceChange} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Internal reference" data-id="element-1762" />
          </div>
        </div>
      </div>
      {/* Billing Schedule */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1763">
        <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1764">
          <ClockIcon size={20} className="mr-2 text-blue-600" data-id="element-1765" />
          Billing Schedule
        </h3>
        <div className="space-y-4" data-id="element-1766">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1767">
            <div data-id="element-1768">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1769">
                Billing Interval
              </label>
              <select value={billingInterval} onChange={e => setBillingInterval(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1770">
                <option value="weekly" data-id="element-1771">Weekly</option>
                <option value="biweekly" data-id="element-1772">Every 2 Weeks</option>
                <option value="monthly" data-id="element-1773">Monthly</option>
                <option value="quarterly" data-id="element-1774">Quarterly</option>
                <option value="biannually" data-id="element-1775">Every 6 Months</option>
                <option value="annually" data-id="element-1776">Annually</option>
              </select>
            </div>
            <div data-id="element-1777">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1778">
                Number of Billing Cycles (Optional)
              </label>
              <input type="number" min="1" value={billingCycles} onChange={e => setBillingCycles(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Leave blank for unlimited" data-id="element-1779" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1780">
                Leave blank for an ongoing subscription
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1781">
            <div data-id="element-1782">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1783">
                Start Date
              </label>
              <div className="relative mt-1 rounded-md shadow-sm" data-id="element-1784">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1785">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-1786" />
                </div>
                <input type="text" value={startDate} onChange={e => setStartDate(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="MM/DD/YYYY" data-id="element-1787" />
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1788">
                Leave blank to start immediately
              </p>
            </div>
            <div data-id="element-1789">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1790">
                Trial Period (Days)
              </label>
              <input type="number" min="0" value={trialDays} onChange={e => setTrialDays(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0" data-id="element-1791" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-1792">
                Free trial before first payment
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Subscription Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-1793">
        <h3 className="text-md font-medium text-gray-800 mb-2" data-id="element-1794">
          Subscription Summary
        </h3>
        <div className="space-y-2" data-id="element-1795">
          <div className="flex justify-between" data-id="element-1796">
            <span className="text-sm text-gray-600" data-id="element-1797">Amount:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1798">
              {amount ? `$${amount} ${currency}` : '-'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-1799">
            <span className="text-sm text-gray-600" data-id="element-1800">Billing Interval:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1801">
              {billingInterval === 'weekly' ? 'Weekly' : billingInterval === 'biweekly' ? 'Every 2 Weeks' : billingInterval === 'monthly' ? 'Monthly' : billingInterval === 'quarterly' ? 'Every 3 Months' : billingInterval === 'biannually' ? 'Every 6 Months' : 'Annually'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-1802">
            <span className="text-sm text-gray-600" data-id="element-1803">Duration:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1804">
              {billingCycles ? `${billingCycles} billing cycles` : 'Ongoing'}
            </span>
          </div>
          {parseInt(trialDays) > 0 && <div className="flex justify-between" data-id="element-1805">
              <span className="text-sm text-gray-600" data-id="element-1806">Trial Period:</span>
              <span className="text-sm font-medium text-gray-800" data-id="element-1807">
                {trialDays} days
              </span>
            </div>}
          <div className="flex justify-between" data-id="element-1808">
            <span className="text-sm text-gray-600" data-id="element-1809">Start Date:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1810">
              {startDate || 'Immediate'}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-1811">
            <span className="text-sm text-gray-600" data-id="element-1812">Customer:</span>
            <span className="text-sm font-medium text-gray-800" data-id="element-1813">
              {subscriptionMode === 'existing' ? selectedCustomer ? selectedCustomer.name : 'Not selected' : customerEmail ? customerName || customerEmail : 'Not specified'}
            </span>
          </div>
          {subscriptionMode === 'existing' && selectedCustomer && selectedCard && <div className="flex justify-between" data-id="element-1814">
                <span className="text-sm text-gray-600" data-id="element-1815">Payment Method:</span>
                <span className="text-sm font-medium text-gray-800" data-id="element-1816">
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.brand}{' '}
                  ••••
                  {selectedCustomer.cards.find(card => card.id === selectedCard)?.last4}
                </span>
              </div>}
        </div>
      </div>
    </div>;
}